/**
 * TankActionState Mirror Helpers — Phase 1 dual-write layer.
 *
 * Each mirror function sets p.actionState from current boolean flags.
 * Called at every site that mutates the flags. assertActionStateConsistent()
 * runs once per player per physics frame in DEV to verify consistency.
 *
 * Zero behavior change — all existing code continues reading boolean flags.
 */

import type { Player, TankActionState, TumbleVariant } from '../types';

// ─── Previous State Tracking (DEV-only) ─────────────────────────────────────

/** Per-player previous actionState for debugging transitions that led to warnings. */
const prevStates: Map<Player, { tag: string; detail: string }> = new Map();

function stateDetail(as: TankActionState): string {
  switch (as.tag) {
    case 'settled': return `settled(${as.settledX.toFixed(0)},${as.settledY.toFixed(0)})`;
    case 'walking': return `walking(catchup=${as.catchupTimer})`;
    case 'airborne': return 'airborne';
    case 'tumble_hitstun': return `tumble_hitstun(${as.variant} h=${as.hitstun} a=${as.armor})`;
    case 'tumble_recovery': return `tumble_recovery(${as.variant} f=${as.recoveryFrame}/${as.recoveryMax})`;
    case 'di_frozen': return `di_frozen(${as.pendingVx.toFixed(1)},${as.pendingVy.toFixed(1)})`;
    case 'defensive_frozen': return 'defensive_frozen';
    case 'dead': return 'dead';
  }
}

// ─── Mirror Helpers ──────────────────────────────────────────────────────────

export function mirrorEnterTumble(p: Player, variant: TumbleVariant): void {
  // enterTumble() is speed-gated — if the gate rejected, tumble is still false.
  if (!p.tumble) return;
  p.actionState = {
    tag: 'tumble_hitstun',
    hitstun: p.hitstunFrames,
    armor: p.launchArmor,
    variant,
  };
}

export function mirrorEnterRecovery(p: Player, variant: TumbleVariant): void {
  p.actionState = {
    tag: 'tumble_recovery',
    recoveryFrame: p.tumbleRecoveryFrame,
    recoveryMax: p.tumbleRecoveryMax,
    variant,
  };
}

export function mirrorExitTumble(p: Player): void {
  p.actionState = { tag: 'airborne' };
}

export function mirrorSettle(p: Player): void {
  p.actionState = { tag: 'settled', settledX: p.settledX, settledY: p.settledY };
}

export function mirrorWalking(p: Player): void {
  p.actionState = { tag: 'walking', catchupTimer: p.walkCatchupTimer };
}

export function mirrorAirborne(p: Player): void {
  p.actionState = { tag: 'airborne' };
}

export function mirrorDIFreeze(p: Player): void {
  p.actionState = { tag: 'di_frozen', pendingVx: p.diPendingVx, pendingVy: p.diPendingVy };
}

export function mirrorDead(p: Player): void {
  p.actionState = { tag: 'dead' };
}

// ─── DEV Assertion ───────────────────────────────────────────────────────────

/**
 * Verify actionState matches boolean flags. DEV-only, runs once per player
 * per physics frame. Console warns on mismatch with full state dump.
 */
export function assertActionStateConsistent(p: Player, label?: string): void {
  if (!import.meta.env.DEV) return;

  const as = p.actionState;
  const prefix = label ? `[actionState ${label}]` : '[actionState]';

  // Determine expected tag from boolean flag hierarchy:
  // dead > di_frozen > settled > tumble > walking > airborne
  let expectedTag: string;
  if (!p.alive) {
    expectedTag = 'dead';
  } else if (p.diPendingVx !== 0 || p.diPendingVy !== 0) {
    expectedTag = 'di_frozen';
  } else if (p.settled) {
    expectedTag = 'settled';
  } else if (p.tumble) {
    // Recovery starts at frame 0 when hitstun expires — check hitstunFrames, not recoveryFrame
    expectedTag = (p.hitstunFrames <= 0 && p.tumbleRecoveryMax > 0) ? 'tumble_recovery' : 'tumble_hitstun';
  } else if (p.walkCatchupTimer > 0) {
    // Walking state persists through catchup timer even when walkDeltaX is 0
    expectedTag = 'walking';
  } else {
    expectedTag = 'airborne';
  }

  const prev = prevStates.get(p);
  const prevStr = prev ? prev.detail : '(none)';

  // Tag mismatch check (with tolerances)
  if (as.tag !== expectedTag) {
    // Tolerance: burstFlying=true with tag='airborne' — gold trail persists past tumble exit
    if (expectedTag === 'airborne' && as.tag === 'airborne') {
      // match — no issue
    }
    // Tolerance: pendingDeath=true with tag != 'dead' — death pending, not yet consumed
    else if (expectedTag === 'dead' && p.pendingDeath && p.alive) {
      // pendingDeath set but alive not yet flipped — tolerate current tag
    }
    // Tolerance: defensive_frozen — set externally by defense.ts, not derived from boolean flags
    else if (as.tag === 'defensive_frozen') {
      // defensive_frozen is managed by defense phase, not by boolean flag hierarchy
    }
    // Tolerance: walking tag stale after catchupTimer just expired this frame
    else if (expectedTag === 'airborne' && as.tag === 'walking') {
      // catchup just expired, mirror may not have run yet — tolerate
    }
    else {
      console.warn(
        `${prefix} TAG MISMATCH: tag='${as.tag}' expected='${expectedTag}' prev=${prevStr}`,
        `\n  flags: alive=${p.alive} settled=${p.settled} tumble=${p.tumble} grounded=${p.grounded}`,
        `\n  velocity: vx=${p.vx.toFixed(2)} vy=${p.vy.toFixed(2)} pos=(${p.x.toFixed(0)},${p.y.toFixed(0)})`,
        `\n  detail: diPending=(${p.diPendingVx.toFixed(1)},${p.diPendingVy.toFixed(1)})`,
        `walkCatchup=${p.walkCatchupTimer} burstFlying=${p.burstFlying}`,
        `pendingDeath=${p.pendingDeath} launchArmor=${p.launchArmor}`,
      );
      prevStates.set(p, { tag: as.tag, detail: stateDetail(as) });
      return; // don't check sub-fields on tag mismatch
    }
  }

  // Sub-field checks for matched tags
  if (as.tag === 'tumble_hitstun') {
    if (as.hitstun !== p.hitstunFrames) {
      console.warn(`${prefix} tumble_hitstun.hitstun=${as.hitstun} but p.hitstunFrames=${p.hitstunFrames} prev=${prevStr}`);
    }
    if (as.armor !== p.launchArmor) {
      console.warn(`${prefix} tumble_hitstun.armor=${as.armor} but p.launchArmor=${p.launchArmor} prev=${prevStr}`);
    }
    if (p.burstFlying && as.variant !== 'burst') {
      console.warn(`${prefix} burstFlying=true but variant='${as.variant}' (expected 'burst') prev=${prevStr}`);
    }
    if (p.grappleLaunched && as.variant !== 'grapple') {
      console.warn(`${prefix} grappleLaunched=true but variant='${as.variant}' (expected 'grapple') prev=${prevStr}`);
    }
  }

  if (as.tag === 'tumble_recovery') {
    if (as.recoveryFrame !== p.tumbleRecoveryFrame) {
      console.warn(`${prefix} tumble_recovery.recoveryFrame=${as.recoveryFrame} but p.tumbleRecoveryFrame=${p.tumbleRecoveryFrame} prev=${prevStr}`);
    }
    if (as.recoveryMax !== p.tumbleRecoveryMax) {
      console.warn(`${prefix} tumble_recovery.recoveryMax=${as.recoveryMax} but p.tumbleRecoveryMax=${p.tumbleRecoveryMax} prev=${prevStr}`);
    }
  }

  if (as.tag === 'settled') {
    if (as.settledX !== p.settledX) {
      console.warn(`${prefix} settled.settledX=${as.settledX} but p.settledX=${p.settledX} prev=${prevStr}`);
    }
    if (as.settledY !== p.settledY) {
      console.warn(`${prefix} settled.settledY=${as.settledY} but p.settledY=${p.settledY} prev=${prevStr}`);
    }
  }

  // Snapshot current state for next frame's "prev"
  prevStates.set(p, { tag: as.tag, detail: stateDetail(as) });
}
